import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { setFullViewportWidthStyles, theme, transientOptions } from "shared/lib/styles";

import { FooterVariant } from "scmp-app/components/app-footer";
import { ArticleTrustLabel } from "scmp-app/components/article/article-trust-label";
import {
  Container as ArticleTrustLabelContainer,
  StyledTrustLogo,
} from "scmp-app/components/article/article-trust-label/styles";
import { Container as DownloadAppWidgetContainer } from "scmp-app/components/footer/download-app-widget/styles";
import { Container as NewsletterContainer } from "scmp-app/components/footer/newsletter/styles";
import { Container as SubscriptionWidgetContainer } from "scmp-app/components/footer/subscription-widget/styles";

import { FooterLogo } from "./logo";
import { FooterSitemap } from "./sitemap";
import { SectionLabel, StyledBaseLink } from "./sitemap/styles";
import { FooterSocialMedia } from "./social-media";
type ContainerProps = {
  $variant?: FooterVariant.Dark;
};

export const Container = styled.div<ContainerProps>`
  ${setFullViewportWidthStyles()}
  padding-block-end: 12px;
  padding-inline: 20px;

  ${theme.breakpoints.up("tablet")} {
    padding-inline: 32px;
  }

  ${props =>
    props.$variant === FooterVariant.Dark &&
    css`
      color: #ffffff;

      background-color: #30343c;

      ${SectionLabel}, ${StyledBaseLink} {
        color: #ffffff;
        ${StyledBaseLink} {
          color: #ffffff;
        }
      }

      ${StyledArticleTrustLabel} {
        color: #ffffff;
        ${StyledTrustLogo} {
          path {
            fill: #ffffff;
          }
        }
      }
    `}
`;

type WrapperProps = {
  $variant?: FooterVariant.Dark;
};

export const Wrapper = styled.div<WrapperProps>`
  max-inline-size: 1208px;
  margin-inline: auto;
  padding-block-start: 32px;
  border-block-start: 1px solid #000000;

  ${props =>
    props.$variant === FooterVariant.Dark &&
    css`
      border-block-start: 1px solid #ffffff;
    `}
`;

export const Header = styled.div`
  display: grid;
  grid-template-areas:
    "Logo"
    "SocialMedia";

  padding-block-end: 24px;

  ${theme.breakpoints.only("mobile")} {
    justify-content: center;
  }

  ${theme.breakpoints.up("tablet")} {
    grid-template-areas: "Logo SocialMedia";
    grid-template-columns: max-content 1fr;
    gap: 40px;

    padding-block-end: 32px;
  }
`;

export const StyledFooterLogo = styled(FooterLogo)`
  grid-area: Logo;

  ${theme.breakpoints.only("mobile")} {
    margin-block-end: 16px;
  }
`;

export const StyledFooterSocialMedia = styled(FooterSocialMedia)`
  grid-area: SocialMedia;

  ${theme.breakpoints.only("mobile")} {
    justify-content: center;
  }
`;

type ContentProps = {
  $isScmpSubscriber: boolean;
};
export const Content = styled("div", { ...transientOptions })<ContentProps>`
  display: grid;
  grid:
    "Sitemap"
    "Subscription"
    "Newsletter"
    "DownloadApp";

  padding-block-end: 32px;

  ${theme.breakpoints.up("tablet")} {
    ${props =>
      props.$isScmpSubscriber
        ? css`
            grid:
              "Sitemap Sitemap"
              "Newsletter DownloadApp"
              / 1fr 1fr;
          `
        : css`
            grid:
              "Sitemap Sitemap Sitemap"
              "Subscription Newsletter DownloadApp"
              / 1fr 1fr 1fr;
          `}
  }

  ${theme.breakpoints.up("desktop")} {
    grid:
      "Sitemap . Subscription"
      "Sitemap . Newsletter"
      "Sitemap . DownloadApp" 1fr
      / 1fr 40px 300px;
  }
`;

export const StyledFooterSitemap = styled(FooterSitemap)`
  grid-area: Sitemap;
  ${theme.breakpoints.up("tablet")} {
    padding-inline-end: 20px;
  }
  ${theme.breakpoints.up("desktop")} {
    padding-inline-end: 0;
  }
`;

const wrapperStyle = css`
  max-inline-size: 400px;
  margin-block: 0;
  margin-inline: auto;
`;
export const FooterSubscriptionWidgetContainer = styled.div`
  grid-area: Subscription;
  border-block-end: 1px solid rgba(0, 0, 0, 0.2);

  margin-block-end: 12px;
  padding-block-end: 12px;

  ${theme.breakpoints.only("mobile")} {
    ${SubscriptionWidgetContainer} {
      ${wrapperStyle}
    }
  }

  ${theme.breakpoints.between("tablet", "desktop")} {
    margin-block-end: 0;
    margin-inline-end: 12px;
    padding-block-end: 0;
    padding-inline-end: 12px;
    border-inline-end: 1px solid rgba(0, 0, 0, 0.2);
    border-block-end: none;
  }
`;

export const FooterNewsletterContainer = styled.div`
  grid-area: Newsletter;

  margin-block-end: 12px;
  padding-block-end: 12px;
  border-block-end: 1px solid rgba(0, 0, 0, 0.2);

  ${theme.breakpoints.only("mobile")} {
    ${NewsletterContainer} {
      ${wrapperStyle}
    }
  }

  ${theme.breakpoints.between("tablet", "desktop")} {
    margin-block-end: 0;
    margin-inline-end: 12px;
    padding-block-end: 0;
    padding-inline-end: 12px;
    border-inline-end: 1px solid rgba(0, 0, 0, 0.2);
    border-block-end: none;
  }
`;

export const FooterDownloadAppWidgetContainer = styled.div`
  grid-area: DownloadApp;
  ${theme.breakpoints.only("mobile")} {
    ${DownloadAppWidgetContainer} {
      ${wrapperStyle}
    }
  }
`;

export const FooterBottom = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: space-between;
  align-items: center;

  padding-block-start: 12px;

  border-block-start: 1px solid #000000;

  ${theme.breakpoints.up("tablet")} {
    flex-direction: row;
    gap: 0;
  }
`;

export const Copyright = styled.div`
  font-weight: 400;
  font-size: 12px;
`;

export const StyledArticleTrustLabel = styled(ArticleTrustLabel)`
  color: #000000;
  font-size: 12px;
  line-height: 100%;
  text-decoration: none;

  ${ArticleTrustLabelContainer} {
    flex-direction: row-reverse;
    align-items: center;
  }

  ${StyledTrustLogo} {
    path {
      fill: #000000;
    }
  }
`;
